import React from "react";
import { Text, Tag, Link } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { JIRA_BASE_URL } from "../../constants";
import {
  extractDescriptionAfterColon,
  formatChildWorkItemStatus,
  getStatusTagVariant,
  isTaskCompleted,
} from "../../WorkflowDetailsDrawer/TasksTab/utils";
import * as styles from "./styles_vanilla.css";
import type { JiraChildWorkItem } from "../hooks/useJiraChildWorkItems";

type JiraTaskRowProps = {
  item: JiraChildWorkItem;
  /** Zero-based position; the step indicator shows index + 1. */
  index: number;
  /** Canopy shows the raw Jira status + a link-to-Jira column; GPX uses the status category. */
  isCanopy: boolean;
};

export const JiraTaskRow: React.FC<JiraTaskRowProps> = ({ item, index, isCanopy }) => {
  const effectiveStatus = isCanopy ? item.status : (item.statusCategoryName ?? item.status);
  const completed = isTaskCompleted(effectiveStatus);

  return (
    <tr className={styles.taskTableRow}>
      <td className={styles.taskTableCell}>
        <div className={styles.taskNameCell}>
          <span
            className={`${styles.stepNumber} ${
              completed ? styles.stepNumberComplete : styles.stepNumberInProgress
            }`}
          >
            {completed ? (
              <Icon type="check" variant="outline" color={Colors.Neutral0} size="small" />
            ) : (
              index + 1
            )}
          </span>
          <Text variant="regular-md" color={Colors.Neutral900}>
            {extractDescriptionAfterColon(item.summary)}
          </Text>
        </div>
      </td>
      <td className={styles.taskTableCell}>
        <Text variant="regular-md" color={Colors.Neutral900}>
          {item.responsibleParty || "-"}
        </Text>
      </td>
      <td className={styles.taskTableCell}>
        <Text variant="regular-md" color={Colors.Neutral900}>
          {item.assignedTo || "Unassigned"}
        </Text>
      </td>
      <td className={styles.taskTableCell}>
        <Tag
          variant={getStatusTagVariant(effectiveStatus)}
          label={formatChildWorkItemStatus(effectiveStatus)}
        />
      </td>
      {isCanopy && (
        <td className={styles.taskTableCell}>
          <Link
            href={`${JIRA_BASE_URL}/browse/${item.key}`}
            shouldOpenInNewTab
            icon="external-link"
            iconPosition="right"
            iconVariant="outline"
            iconSize="small"
            align="left"
          >
            {item.key}
          </Link>
        </td>
      )}
    </tr>
  );
};

export default JiraTaskRow;
